import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React from 'react';
import {
  PRICING_TABLE_TOOLTIPS,
  SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE,
  SUBSCRIPTION_TABLE_ROWS,
  SUBSCRIPTION_TABLE_ROWS_FOR_MOBILE_VIEW,
  exchangeRates,
} from 'src/const';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { Box, Button, Typography } from '@mui/material';
import {
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  SIDEBAR_COLOR,
  SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
} from 'src/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  calculateCouponChange,
  checkPromocodeValidity,
  formatCurrencyBasedOnCountryCode,
} from 'src/utils/functions';
import dottedImage from '../../../assets/SVG_DOTTED_SEC.svg';
import check from '../../../assets/check.svg';
import { useDispatch } from 'react-redux';
import { setLeadFormSource } from 'src/store/Dashboard/actions';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { InfoButton } from 'src/App/Components/Buttons';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import InfoTooltipContent from 'src/App/HOC/Tooltips/InfoTooltipContent';

function SubscriptionModalDataGrid({
  matches,
  userLocalCurrency,
  user,
  duration,
  subscriptionPriceAccordingToUserLocalCurrency,
  promoCodeDiscountsDict,
  product,
  setProduct,
  promoCode,
  isPromoCodeValid,
  openDemoBookingForm,
  paymentModalRef,
  setFormHeadingFor,
  setShowUpgradeSubscriptionModal,
  isMobile,
}) {
  const dispatch = useDispatch();
  const currentActivePlan = user.subscriptionPlan;
  const easeInOutQuad = t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  const subscriptionTableColumn: GridColDef[] = [
    {
      field: 'feature',
      headerName: 'Feature',
      width: 350,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <Box sx={{ height: '100%', width: '100%' }}>
          {params.value == 'Null' ? (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <img src={dottedImage} />
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                backgroundColor: COLOR_PRIMARY_2,
                color: SIDEBAR_COLOR,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                {params.value}
                <InfoButton
                  tooltipContent={
                    <InfoTooltipContent
                      content={PRICING_TABLE_TOOLTIPS[params.row.featureTooltip]?.content}
                    />
                  }
                  iconColor="#fff"
                />
              </Typography>
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'starter',
      headerName: 'Insight Starter',
      width: 290,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <CustomCellRenderElement params={params} currency={userLocalCurrency} />
      ),
    },
    // {
    //   field: 'pro',
    //   headerName: 'Insight Pro',
    //   width: 220,
    //   editable: false,
    //   sortable: true,
    //   renderCell: (params: any) => (
    //     <CustomCellRenderElement params={params} currency={userLocalCurrency} />
    //   ),
    // },
    {
      field: 'expert',
      headerName: 'Analytics Expert',
      width: 290,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <CustomCellRenderElement params={params} currency={userLocalCurrency} />
      ),
    },
    {
      field: 'scale',
      headerName: 'Market Intelligence Scale',
      width: 290,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <CustomCellRenderElement params={params} currency={userLocalCurrency} />
      ),
    },
  ];

  const tableCellValueMapper = {
    yes: <img src={check} alt="check" style={{ height: '20px', width: '20px' }} />,
    no: '',
    comingSoon: (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img src={check} alt="check" style={{ height: '20px', width: '20px' }} />
        <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>Coming soon</Typography>
      </Box>
    ),
    top10: 'Top 10',
    limited: 'Limited',
    year1: '1 year',
    year2: '2 year',
    year3: '3 year',
    through2021: 'Through 2021',
    allMarkets: 'All Markets Worldwide',
    'Historical Data': 'Historical Data',
    allHosts: (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>All</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>20 Markets</Typography>
      </Box>
    ),
  };

  const headingMapper = {
    'Insight Starter': {
      heading: 'Insight',
      subHeading: 'Starter',
    },
    'Insight Pro': {
      heading: 'Insight',
      subHeading: 'Pro',
    },
    'Analytics Expert': {
      heading: 'Analytics',
      subHeading: 'Expert',
    },
    'Market Intelligence Scale': {
      heading: 'Market',
      subHeading: 'Intelligence',
      subHeading2: 'Scale',
    },
  };

  const productDescriptions = {
    'Insight Starter': 'Ideal for individuals estimating Airbnb revenue or exploring a new market.',
    'Insight Pro':
      'Perfect for investors seeking deeper market insights and detailed property metrics.',
    'Analytics Expert':
      'Designed for investors aiming to maximize ROI by understanding micro-neighborhood-level trends and demand.',
    'Market Intelligence Scale':
      'A tailored plan for growing businesses with comprehensive property-level data.',
  };

  const getProductName = value => {
    if (value.includes('Pro')) {
      return 'pro';
    } else if (value.includes('Expert')) {
      return 'expert';
    } else {
      return 'scale';
    }
  };

  const handleBuyNowBtnclick = value => {
    if (value == 'Insight Starter') {
      return;
    }

    if (value == 'Insight Pro') {
      setProduct('pro');
    }
    if (value == 'Analytics Expert') {
      setProduct('expert');
    }
    if (value == 'Market Intelligence Scale') {
      setProduct('scale');
    }

    if (
      (['Analytics Expert', 'Market Intelligence Scale'].includes(value) &&
        currentActivePlan == 'pro') ||
      (value == 'Market Intelligence Scale' && currentActivePlan == 'expert')
    ) {
      setShowUpgradeSubscriptionModal(getProductName(value));
      return;
    }

    if (value != 'Insight Starter') {
      if (paymentModalRef.current) {
        const modal = paymentModalRef.current;
        const scrollHeight = modal.scrollHeight;
        const duration = 500; // Adjust the duration as needed (in milliseconds)
        const startTime = performance.now();
        const startScroll = modal.scrollTop;

        const targetScroll = scrollHeight - modal.clientHeight - (isMobile ? 750 : 250);

        const scrollStep = () => {
          const now = performance.now();
          const time = Math.min(1, (now - startTime) / duration);
          modal.scrollTop = startScroll + (targetScroll - startScroll) * easeInOutQuad(time);

          if (time < 1) {
            requestAnimationFrame(scrollStep);
          }
        };

        requestAnimationFrame(scrollStep);
      }
    }
  };

  const CustomCellRenderElement = ({ params, currency }) => {
    let productName = getProductName(params.value);
    let price: any = 0;
    let priceDiscountedMonthly: any = 0;
    let priceDiscountedYearly: any = 0;
    if (params.value != 'Insight Starter') {
      price =
        duration == 'monthly'
          ? formatCurrencyBasedOnCountryCode(
              parseFloat(
                subscriptionPriceAccordingToUserLocalCurrency[productName]['monthly'].toFixed(2)
              ),
              userLocalCurrency
            )
          : formatCurrencyBasedOnCountryCode(
              parseFloat(
                (
                  subscriptionPriceAccordingToUserLocalCurrency[productName][duration] / 12
                ).toFixed()
              ),
              userLocalCurrency
            );

      if (isPromoCodeValid) {
        let dPrice: any = calculateCouponChange(
          promoCode,
          promoCodeDiscountsDict,
          subscriptionPriceAccordingToUserLocalCurrency,
          duration,
          () => {},
          productName
        );

        priceDiscountedMonthly = formatCurrencyBasedOnCountryCode(
          parseFloat((dPrice / 12).toFixed(2)),
          userLocalCurrency
        );
        priceDiscountedYearly = formatCurrencyBasedOnCountryCode(dPrice, userLocalCurrency);
      }
    }

    const isCurrentPlanSubscribed = (value, currentActivePlan) => {
      if (currentActivePlan == 'starter') {
        if (value == 'Insight Starter') return true;
      }
      if (currentActivePlan == 'pro') {
        if (value == 'Insight Pro') return true;
      }
      if (currentActivePlan == 'expert') {
        if (value == 'Analytics Expert') return true;
      }
      if (currentActivePlan == 'scale') {
        if (value == 'Market Intelligence Scale') return true;
      }
      return false;
    };

    let isPlanSubscribed = isCurrentPlanSubscribed(params.value, currentActivePlan);

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        {params.id == 0 ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              position: 'relative',
              boxSizing: 'border-box',
              padding: '10px',
            }}
          >
            <Box sx={{ height: '30px' }}>
              {params.value == 'Analytics Expert' && (
                <Box
                  sx={{
                    backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
                    padding: '5px 10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0px 7.5%',
                  }}
                >
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: SIDEBAR_COLOR }}>
                    POPULAR
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                height: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography style={{ fontWeight: 700, fontSize: '20px' }}>
                {headingMapper[params.value]['heading']}
              </Typography>
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: '20px',
                  marginTop: '-8px',
                  color:
                    params.value == 'Market Intelligence Scale' ? COLOR_TEXT_1 : COLOR_PRIMARY_2,
                }}
              >
                {headingMapper[params.value]['subHeading']}
              </Typography>
              {headingMapper[params.value]['subHeading2'] && (
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: '20px',
                    color: COLOR_PRIMARY_2,
                    marginTop: '-8px',
                  }}
                >
                  {headingMapper[params.value]['subHeading2']}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                height: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 500,
                      textDecoration:
                        isPromoCodeValid && params.value !== 'Insight Starter'
                          ? 'line-through'
                          : 'none',
                      fontSize:
                        isPromoCodeValid && params.value !== 'Insight Starter' ? '14px' : '',
                    }}
                  >
                    {exchangeRates[currency].symbol}
                    {price}
                  </Typography>
                  {isPromoCodeValid && params.value !== 'Insight Starter' && (
                    <Typography
                      variant="h6"
                      style={{
                        fontWeight: 500,
                        textDecoration: 'none',
                        marginLeft: '5px',
                      }}
                    >
                      {exchangeRates[currency].symbol}
                      {duration == 'yearly' ? priceDiscountedMonthly : priceDiscountedYearly}
                    </Typography>
                  )}
                  <span
                    style={{
                      fontSize: '14px',
                      color: COLOR_GREY_600,
                      textDecoration: 'none !important',
                    }}
                  >
                    /month
                  </span>
                </Box>
                {duration == 'yearly' && params.value !== 'Insight Starter' && (
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Billed Annually:{' '}
                    {isPromoCodeValid
                      ? priceDiscountedYearly
                      : exchangeRates[currency].symbol +
                        formatCurrencyBasedOnCountryCode(
                          parseFloat(
                            subscriptionPriceAccordingToUserLocalCurrency[productName][
                              'yearly'
                            ].toFixed(2)
                          ),
                          userLocalCurrency
                        )}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                height: '30px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                className="mapFilterButton"
                size="small"
                sx={{
                  fontSize: '16px', // Added units for font size
                  border: isPlanSubscribed ? `1px solid ${COLOR_PRIMARY_2}` : 'none',
                  background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
                  color: isPlanSubscribed ? COLOR_PRIMARY_2 : '#ffffff',
                  '&:hover': {
                    background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
                  },
                  width: '85%',
                }}
                onClick={() => {
                  handleBuyNowBtnclick(params.value);
                }}
              >
                <Typography style={{ display: 'flex', alignItems: 'center' }}>
                  {isPlanSubscribed ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 16, marginRight: '5px' }} /> Your Current
                      Plan
                    </>
                  ) : params.value === 'Insight Starter' ? (
                    'Try for free'
                  ) : (currentActivePlan === 'pro' &&
                      (params.value === 'Analytics Expert' ||
                        params.value === 'Market Intelligence Scale')) ||
                    (currentActivePlan === 'expert' &&
                      params.value === 'Market Intelligence Scale') ? (
                    'Upgrade'
                  ) : (
                    'Buy Now'
                  )}
                </Typography>
              </Button>
            </Box>
            <Box
              sx={{
                height: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                style={{
                  // padding: '5px 5px 0px 10px',
                  marginTop: params.value == 'Analytics Expert' ? '10px' : 'none',
                  maxWidth: '85%',
                  fontSize: '10px',
                  color: COLOR_TEXT_2,
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              >
                {productDescriptions[params.value]}
              </Typography>
            </Box>
          </Box>
        ) : !matches || params.id % 2 == 0 ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              // borderRight: params.field == 'scale' ? '2px solid black' : '',
              boxSizing: 'border-box',
            }}
          >
            <Typography>
              {params.value in tableCellValueMapper
                ? tableCellValueMapper[params.value]
                : params.value}
            </Typography>
          </Box>
        ) : params.field == 'starter' ? (
          <Box
            sx={{
              height: '100%',
              width: `calc(${SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE} - 2px)`,
              display: 'flex',
              // justifyContent: 'center',
              alignItems: 'center',
              // borderRight: '2px solid black',
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 10,
              backgroundColor: '#F5F5F5',
            }}
          >
            <Typography
              style={{
                fontWeight: 500,
                position: 'sticky',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    );
  };

  return (
    <DataGrid
      sx={{
        backgroundColor: 'white',
        border: 'none',
        overflow: 'visible',
        '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell': {
          overflow: 'visible !important',
        },
        '& .MuiDataGrid-columnHeaders': {
          display: 'none',
          minHeight: 0,
          height: 0,
        },
        '& .MuiDataGrid-footerContainer': {
          display: 'none',
          minHeight: 0,
          height: 0,
        },
        '& .MuiDataGrid-virtualScroller ': {
          margin: '0px !important',
          // borderBottom: '1px solid black',
        },
        '& .MuiDataGrid-main': {
          minHeight: 0,
          height: 'calc(100% - 0px)',
        },
        '& .MuiDataGrid-cell': {
          padding: 0,
          outline: 'none',
          display: 'block',
          whiteSpace: 'normal !important',
          wordBreak: 'break-word',
          lineHeight: '1',
        },
        '& .MuiDataGrid-row': {
          overflow: 'visible  !important',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'inherit',
        },
        '& .even-row': {
          backgroundColor: '#ffffff',
        },
        '& .odd-row': {
          backgroundColor: '#F5F5F5',
        },
      }}
      rows={SUBSCRIPTION_TABLE_ROWS}
      columns={subscriptionTableColumn}
      disableColumnFilter={true}
      disableColumnMenu={true}
      disableSelectionOnClick
      getRowHeight={params => (params.id === 0 ? 270 : 50)}
      getRowClassName={params =>
        typeof params.id === 'number' && params.id % 2 === 0 ? 'even-row' : 'odd-row'
      }
    />
  );
}

export default SubscriptionModalDataGrid;
